import React from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import remarkDirective from "remark-directive";
import { visit } from "unist-util-visit";
import {
  Box,
  Link as MuiLink,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { Prism as SyntaxHighlighter } from "react-syntax-highlighter";
import { vscDarkPlus } from "react-syntax-highlighter/dist/esm/styles/prism";

// Remark plugin: converts :::details[Title] ... ::: into a custom node
function remarkAccordion() {
  return (tree) => {
    visit(tree, (node) => {
      if (
        node.type === "containerDirective" &&
        node.name === "details"
      ) {
        node.data = node.data || {};
        node.data.hName = "details";
        const label =
          node.children[0]?.data?.directiveLabel
            ? node.children.shift()
            : null;
        node.data.hProperties = {
          summary: label
            ? label.children.map((c) => c.value || "").join("")
            : "Details",
        };
      }
    });
  };
}

export default function Markdown({ text }) {  
  return (
    <Box
      sx={{
        "& h1,& h2,& h3": { mt: 2, mb: 1, fontWeight: 600 },
        "& p": { my: 1 },
        "& ul, & ol": { pl: 3, my: 1 },
        "& code": {
          px: 0.5,
          py: 0.25,
          borderRadius: 1,
          // bgcolor: "grey.100",
          fontFamily:
            "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
        },
        "& pre": {
          p: 1.5,
          borderRadius: 2,
          // bgcolor: "grey.100",
          overflow: "auto",
        },
        "& img": {
         width: "100%",
        },
        "& table": { borderCollapse: "collapse", my: 1 },
        "& th, & td": { border: "1px solid", borderColor: "divider", p: 1 },
      }}
    >
      <ReactMarkdown
        remarkPlugins={[remarkGfm, remarkDirective, remarkAccordion]}
        components={{
          details: ({ summary, children }) => (
            <Accordion
              disableGutters
              elevation={0}
              sx={{
                my: 0.5,
                border: "1px solid",
                borderColor: "divider",
                borderRadius: "6px !important",
                "&:before": { display: "none" },
                bgcolor: "transparent",
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon sx={{ fontSize: 16 }} />}
                sx={{ minHeight: 32, py: 0, px: 1.5, "& .MuiAccordionSummary-content": { my: 0.5 } }}
              >
                <Typography variant="body2" fontWeight={600} fontSize={13}>{summary}</Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ px: 1.5, pt: 0, pb: 1 }}>{children}</AccordionDetails>
            </Accordion>
          ),
          a: ({ node, ...props }) => (
            <MuiLink {...props} target="_blank" rel="noopener noreferrer" />
          ),
          code({ inline, className, children, ...props }) {
            const match = /language-(\w+)/.exec(className || "");
            if (!inline && match) {
              return (
                <SyntaxHighlighter
                  language={match[1]}
                  PreTag="div"
                  style={vscDarkPlus}
                  {...props}
                >
                  {String(children).replace(/\n$/, "")}
                </SyntaxHighlighter>
              );
            }
            return (
              <code className={className} {...props}>
                {children}
              </code>
            );
          },
        }}
      >
        {text}
      </ReactMarkdown>
    </Box>
  );
}
